import { useRelevantCollections } from 'marketplace-ui-common/collections/hooks/useRelevantCollections';
import { getDataFetchingClientRequestState } from 'marketplace-ui-common/dataFetchingClient/utils/getDataFetchingClientRequestState';
import { useParams } from 'quartz-routing/react-router';
import { useUmbrellaCollections } from '../../collections/hooks/useUmbrellaCollections';
import { useDiscoveryCollections } from '../../collections/hooks/useDiscoveryCollections';
import { useAppContext } from '../../context/AppContext';
import { useGates } from '../../hooks/useGates';
/** Fetches collection based on URL slug or most recent collection, with umbrella collection support */
export function useCollectionQuery(options = {
  mode: 'url'
}) {
  const {
    mode
  } = options;
  const gates = useGates();
  const appContext = useAppContext();
  const {
    collectionSlug
  } = useParams();
  const dsResult = useDiscoveryCollections({
    skip: !gates.newDashboard
  });

  // Always fires; useRelevantCollections has no skip option.
  const publishedCollections = useRelevantCollections({
    appContext,
    offeringType: 'APP'
  });
  const umbrellaCollections = useUmbrellaCollections();
  const umbrellaCollectionsRequestState = getDataFetchingClientRequestState(umbrellaCollections.error, umbrellaCollections.loading);

  // For URL mode without slug, return immediately (matches original behavior)
  if (mode === 'url' && !collectionSlug) {
    return {
      data: undefined,
      error: undefined,
      isUmbrellaCollection: false,
      loading: false
    };
  }
  if (gates.newDashboard) {
    // Umbrella collections stay on the legacy source, so resolve a known
    // umbrella slug before depending on the DS getAllCollections call — a DS
    // outage must not break an otherwise-healthy umbrella page.
    if (mode === 'url' && collectionSlug && umbrellaCollectionsRequestState !== 'PENDING') {
      const umbrellaCollection = umbrellaCollections.getUmbrellaCollectionBySlug(collectionSlug);
      if (umbrellaCollection) {
        return {
          data: umbrellaCollection,
          error: undefined,
          isUmbrellaCollection: true,
          loading: false
        };
      }
    }
    if (dsResult.loading || umbrellaCollectionsRequestState === 'PENDING') {
      return {
        data: undefined,
        error: undefined,
        isUmbrellaCollection: false,
        loading: true
      };
    }
    if (dsResult.error) {
      return {
        data: undefined,
        error: new Error('Failed to load collections'),
        isUmbrellaCollection: false,
        loading: false
      };
    }
    const dsCollection = mode === 'recent' ? dsResult.collections[0] : mode === 'id' ? dsResult.collections.find(c => c.entityId === options.collectionId) : collectionSlug ? dsResult.collections.find(c => c.slug === collectionSlug) : undefined;
    if (dsCollection) {
      var _dsCollection$title, _dsCollection$descrip, _dsCollection$descrip2, _dsCollection$title2, _dsCollection$descrip3;
      return {
        data: {
          id: dsCollection.entityId,
          slug: dsCollection.slug,
          name: dsCollection.name,
          title: (_dsCollection$title = dsCollection.title) !== null && _dsCollection$title !== void 0 ? _dsCollection$title : undefined,
          description: (_dsCollection$descrip = dsCollection.description) !== null && _dsCollection$descrip !== void 0 ? _dsCollection$descrip : '',
          tagline: (_dsCollection$descrip2 = dsCollection.description) !== null && _dsCollection$descrip2 !== void 0 ? _dsCollection$descrip2 : '',
          itemCount: dsCollection.listingCount,
          displayed: true,
          previewItems: dsCollection.previewItems.map(item => ({
            listingId: item.listingId,
            name: item.name,
            iconUrl: item.iconUrl,
            slug: item.slug
          })),
          updatedAt: undefined,
          htmlPageTitle: (_dsCollection$title2 = dsCollection.title) !== null && _dsCollection$title2 !== void 0 ? _dsCollection$title2 : '',
          metaDescription: (_dsCollection$descrip3 = dsCollection.description) !== null && _dsCollection$descrip3 !== void 0 ? _dsCollection$descrip3 : ''
        },
        error: undefined,
        isUmbrellaCollection: false,
        loading: false
      };
    }
    return {
      data: undefined,
      error: new Error(mode === 'recent' ? 'No collections available' : 'Collection not found'),
      isUmbrellaCollection: false,
      loading: false
    };
  }

  // Legacy path
  if (publishedCollections.loading || umbrellaCollectionsRequestState === 'PENDING') {
    return {
      data: undefined,
      error: undefined,
      isUmbrellaCollection: false,
      loading: true
    };
  }
  if (publishedCollections.error) {
    return {
      data: undefined,
      error: new Error('Failed to load collections'),
      isUmbrellaCollection: false,
      loading: false
    };
  }
  if (umbrellaCollectionsRequestState === 'FAILED') {
    return {
      data: undefined,
      error: new Error('Failed to load umbrella collections'),
      isUmbrellaCollection: false,
      loading: false
    };
  }

  // Get collection based on mode - consolidating the helper function logic
  const publishedCollection = mode === 'recent' ? publishedCollections.getDisplayedCollections()[0] : mode === 'id' ? publishedCollections.getCollectionById(options.collectionId) : collectionSlug ? publishedCollections.getCollectionBySlug(collectionSlug) : undefined;
  if (publishedCollection) {
    return {
      data: publishedCollection,
      error: undefined,
      isUmbrellaCollection: false,
      loading: false
    };
  }

  // If no regular collection found and we're in URL mode, check for umbrella collections
  if (mode === 'url' && collectionSlug) {
    const umbrellaCollection = umbrellaCollections.getUmbrellaCollectionBySlug(collectionSlug);
    if (umbrellaCollection) {
      return {
        data: umbrellaCollection,
        error: undefined,
        isUmbrellaCollection: true,
        loading: false
      };
    }
  }

  // Neither regular nor umbrella collection found (or recent mode with no collections)
  return {
    data: undefined,
    error: new Error(mode === 'recent' ? 'No collections available' : 'Collection not found'),
    isUmbrellaCollection: false,
    loading: false
  };
}